import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ApiInspector from '../components/ApiInspector';
import { usePermesso } from '../context/PermessiContext';
import { useFiltroSocieta } from '../hooks/useFiltroSocieta';
import { apiFetch } from '../lib/api';
import {
  TICKET_TIPO_LABEL, TICKET_PRIORITA_LABEL,
  ticketStatoBadge, ticketPrioritaBadge, ticketStatoTesto,
  TicketPriorita, TicketSummary,
} from './assistenza/ticketAssistenza';
import StoricoSistemaModal, { StoricoSistema, HostInfo } from '../components/StoricoSistemaModal';
import { formatMB, formatDataOra, formatKernel, formatIntero } from '../utils/formatSistema';
import { API_DOCS } from './DashboardPage.api';
import './DashboardPage.css';

interface MsgRicevuto {
  id: number;
  oggetto: string;
  priorita: string;
  createdAt: string;
  mittente_nome: string;
  stato: 'DA_LEGGERE' | 'LETTO';
}

const PRIORITA_COLOR: Record<string, string> = { URGENTE: '#ef4444', ALTA: '#f97316', MEDIA: '#3b82f6', BASSA: '#6b7280' };
const PRIORITA_LABEL: Record<string, string> = { URGENTE: 'Urgente', ALTA: 'Alta', MEDIA: 'Media', BASSA: 'Bassa' };

const PESO_PRIORITA: Record<TicketPriorita, number> = { BLOCCANTE: 0, URGENTE: 1, MEDIA: 2, BASSA: 3 };

// La valutazione dello stato (recente / fallito / troppo vecchio) la fa il
// backend e arriva gia' pronta in `salute`: la soglia deve vivere in un punto
// solo, altrimenti server e client finiscono per rispondere in modo diverso
// alla stessa domanda.
type SaluteBackup = 'OK' | 'ERRORE' | 'SCADUTO';

interface BackupInfo {
  esito: 'OK' | 'ERRORE' | null;
  salute: SaluteBackup;
  eseguitoIl: string | null;
  dimensione: number | null;
  numeroFile: number | null;
  messaggio: string | null;
}
interface RestoreInfo {
  esito: 'OK' | 'ERRORE' | null;
  salute: SaluteBackup;
  eseguitoIl: string | null;
  durataSec: number | null;
  tabelleRipristinate: number | null;
  tabelleProduzione: number | null;
  tabelleMancanti: number | null;
  tabelleAllineate: number | null;
  righeRipristinate: number | null;
  schemaPiuRecente: boolean;
  messaggio: string | null;
}

interface StatoSistema {
  versione: { build: string | null; rilasciatoIl: string | null; commit: string | null };
  backup: { oreSoglia: number; database: BackupInfo; documenti: BackupInfo };
  restore: RestoreInfo;
  host: HostInfo;
}

// La macchina di backup non ha ragione di parlare di se' se non quando qualcosa
// non va: il 15/08/2026 ha installato un kernel e non lo ha mai avviato, e per
// due giorni la dashboard non aveva modo di dirlo.
function descrizioneHost(h: HostInfo): { testo: string; tono: 'errore' | 'ritardo' | 'info' } | null {
  if (h.salute === 'SCADUTO') {
    return {
      testo: h.rilevatoIl
        ? 'La macchina di backup non riporta il suo stato da troppo tempo'
        : 'La macchina di backup non ha mai riportato il suo stato',
      tono: 'ritardo',
    };
  }

  // Protezione disattivata: è un guasto anche se in questo istante non si vede
  // niente di rotto, perché la macchina ha smesso di difendersi da sola.
  if (h.aggiornamentiAutomatici === false) {
    return { testo: 'Aggiornamenti automatici disattivati sulla macchina di backup', tono: 'errore' };
  }
  if (h.riavvioAutomatico === false) {
    return { testo: 'Riavvio automatico disattivato: un kernel aggiornato non entra in servizio', tono: 'errore' };
  }

  if (h.riavvioRichiesto) {
    const giorni = h.riavvioRichiestoDa
      ? Math.floor((Date.now() - new Date(h.riavvioRichiestoDa).getTime()) / 86400000)
      : 0;
    if (giorni > h.giorniSogliaRiavvio) {
      return {
        testo: `Riavvio in attesa da ${giorni} giorni: il riavvio automatico non sta funzionando`,
        tono: 'errore',
      };
    }
    // Attesa normale della finestra notturna: va detto, senza allarmare.
    return {
      testo: h.orarioRiavvio
        ? `Riavvio in attesa, previsto alle ${h.orarioRiavvio}`
        : 'Riavvio in attesa',
      tono: 'info',
    };
  }

  // Kernel nuovo installato ma non avviato senza che dpkg abbia lasciato il
  // flag: raro, ma è esattamente il caso che è passato inosservato.
  if (h.kernelDisallineato) {
    return {
      testo: `Kernel ${formatKernel(h.kernelPiuRecente)} installato ma non in servizio: manca un riavvio`,
      tono: 'errore',
    };
  }

  return null;
}

// Le differenze di righe non sono un guasto: il dump e' la fotografia di un
// istante mentre la produzione continua a cambiare. Il segnale vero e' una
// tabella mancante, che significa ripristino incompleto.
function descrizioneRestore(r: RestoreInfo): string | null {
  const mancanti = r.tabelleMancanti ?? 0;

  // Tabelle create da una migrazione successiva al dump: non sono un guasto,
  // spariscono da sole al primo backup dopo il rilascio. Va detto a parole,
  // altrimenti il numero da solo si legge come un errore.
  if (mancanti > 0 && r.schemaPiuRecente) {
    return mancanti === 1
      ? '1 tabella creata dopo il backup, sarà inclusa dal prossimo'
      : `${mancanti} tabelle create dopo il backup, saranno incluse dal prossimo`;
  }

  if (r.salute === 'ERRORE') {
    if (mancanti > 0) {
      return mancanti === 1 ? '1 tabella non ripristinata' : `${mancanti} tabelle non ripristinate`;
    }
    return r.messaggio || 'Ultima prova di ripristino fallita';
  }
  if (r.salute === 'SCADUTO') {
    return r.eseguitoIl ? 'Prova di ripristino non più recente' : 'Ripristino mai verificato';
  }
  return null;
}

const CLASSE_SALUTE: Record<SaluteBackup, string> = {
  OK: 'ok',
  ERRORE: 'errore',
  SCADUTO: 'ritardo',
};

// Un backup che smette di partire non produce errori: smette e basta. Senza
// questo messaggio la dashboard mostrerebbe l'ultimo esito buono come se
// tutto andasse bene, che e' il modo peggiore di fallire.
function descrizioneSalute(b: BackupInfo, oreSoglia: number): string | null {
  if (b.salute === 'ERRORE') return b.messaggio || 'Ultimo tentativo fallito';
  if (b.salute === 'SCADUTO') {
    return b.eseguitoIl
      ? `Nessun backup da oltre ${oreSoglia} ore`
      : 'Nessun backup mai registrato';
  }
  return null;
}

// ── Integrazioni esterne ────────────────────────────────────────────────────
// Anche qui lo stato arriva già valutato dal backend (services/integrazioni):
// la soglia di 48 ore vive solo lì.
type SaluteIntegrazione = 'OK' | 'ERRORE' | 'SCADUTO' | 'MAI_VERIFICATA' | 'NON_CONFIGURATA';

interface VoceIntegrazione {
  societaId: number | null;
  societaNome: string;
  riferimento: string | null;
  salute: SaluteIntegrazione;
  ultimoAccessoAt: string | null;
  messaggio: string | null;
}
interface Integrazione {
  tipo: string;
  nome: string;
  modulo: string;
  percorso: string;
  // Soglia di silenzio di questa integrazione (il gateway OCPP ha la sua,
  // in frazioni d'ora); null = non scade mai
  sogliaOre?: number | null;
  salute: SaluteIntegrazione;
  ultimoAccessoAt: string | null;
  voci: VoceIntegrazione[];
}
interface StatoIntegrazioni {
  sogliaOre: number;
  integrazioni: Integrazione[];
}

const MODULI_INTEGRAZIONI = [
  'impostazioni.integrazione_ai',
  'impostazioni.integrazione_openapi',
  'dev.api_esterne',
  'impostazioni.integrazione_zoho_sign',
  'impostazioni.integrazione_centralino',
  'aziendale.ricarica.colonnine',
];

const CLASSE_INTEGRAZIONE: Record<SaluteIntegrazione, string> = {
  OK: 'ok',
  ERRORE: 'errore',
  SCADUTO: 'ritardo',
  MAI_VERIFICATA: 'ignoto',
  NON_CONFIGURATA: 'ignoto',
};

function troncato(testo: string, max = 90): string {
  return testo.length > max ? `${testo.slice(0, max - 1)}…` : testo;
}

// Sotto l'ora la soglia si dice in minuti (il gateway OCPP: 35 minuti)
function descrizioneSoglia(ore: number): string {
  if (ore < 1) {
    const minuti = Math.round(ore * 60);
    return minuti === 1 ? '1 minuto' : `${minuti} minuti`;
  }
  return ore === 1 ? '1 ora' : `${ore} ore`;
}

// Il pallino dice quanto è grave, la frase dice dove: con più società la voce
// in errore va nominata, altrimenti bisogna aprire la pagina per scoprirlo.
function descrizioneIntegrazione(i: Integrazione, sogliaGlobale: number): string | null {
  if (i.salute !== 'ERRORE' && i.salute !== 'SCADUTO') return null;
  // Un backend più vecchio non manda la soglia della singola integrazione
  const sogliaOre = i.sogliaOre === undefined ? sogliaGlobale : i.sogliaOre;
  const problemi = i.voci.filter(v => v.salute === i.salute);
  const prefisso = (v: VoceIntegrazione) => (i.voci.length > 1 || v.societaId !== null ? `${v.societaNome}: ` : '');
  const testo = problemi.slice(0, 2).map(v => prefisso(v) + (
    v.salute === 'SCADUTO'
      ? `nessun accesso riuscito${sogliaOre === null ? '' : ` da oltre ${descrizioneSoglia(sogliaOre)}`}`
      : troncato(v.messaggio || 'ultimo accesso fallito')
  ));
  if (problemi.length > 2) testo.push(`e altre ${problemi.length - 2}`);
  return testo.join(' · ');
}

function valoreIntegrazione(i: Integrazione): string {
  if (i.salute === 'NON_CONFIGURATA') return 'non configurata';
  if (!i.ultimoAccessoAt) return i.tipo === 'API_ESTERNE' ? 'mai chiamata' : 'mai verificata';
  return formatDataOra(i.ultimoAccessoAt);
}

function formatDate(d: string) {
  const dt = new Date(d);
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

// Icone inline (riuso lo stile SVG del menu)
const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const IconLifeBuoy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" />
    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" /><line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" /><line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
  </svg>
);
const IconLoader = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
);
const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const { utente, can, gestisceAssistenza } = usePermesso();
  const canMessaggi = can('aziendale.messaggi_lettura', 'view');
  const canAssistenza = can('assistenza.richieste', 'view');

  const [messaggi, setMessaggi] = useState<MsgRicevuto[]>([]);
  const [nonLetti, setNonLetti] = useState(0);
  const [ticket, setTicket] = useState<TicketSummary[]>([]);
  const [sistema, setSistema] = useState<StatoSistema | null>(null);
  const [integrazioni, setIntegrazioni] = useState<StatoIntegrazioni | null>(null);
  const { societaId } = useFiltroSocieta();
  const canIntegrazioni = MODULI_INTEGRAZIONI.some(m => can(m, 'view'));
  const isSuperadmin = utente?.ruolo === 'SUPERADMIN';

  // Storico: caricato solo all'apertura della modale. Sono centinaia di record
  // che la dashboard non mostra, e non vanno pesati su ogni accesso a BOP.
  const [storicoAperto, setStoricoAperto] = useState(false);
  const [storico, setStorico] = useState<StoricoSistema | null>(null);
  const [storicoCaricamento, setStoricoCaricamento] = useState(false);
  const [storicoErrore, setStoricoErrore] = useState<string | null>(null);

  const apriStorico = () => {
    setStoricoAperto(true);
    // Ricarica a ogni apertura: nel frattempo puo' essere passato un giro di
    // backup, e uno storico fermo alla prima apertura della sessione sarebbe
    // peggio che non averlo.
    setStoricoCaricamento(true);
    setStoricoErrore(null);
    // @api GET /api/sistema/storico -> {"success":true,"data":{"giorni":"number","database":[{"id":"number","esito":"string","eseguitoIl":"string","durataSec":"number","dimensione":"number","numeroFile":"number","messaggio":"string"}],"documenti":[{...}],"ripristino":[{"id":"number","esito":"string","eseguitoIl":"string","durataSec":"number","dumpNome":"string","tabelleRipristinate":"number","tabelleProduzione":"number","tabelleMancanti":"number","tabelleAllineate":"number","righeRipristinate":"number","schemaPiuRecente":"boolean","messaggio":"string"}],"aggiornamenti":[{"id":"number","host":"string","giorno":"string","numeroPacchetti":"number","kernelAggiornato":"boolean","kernelVersione":"string","riavviatoDopo":"boolean","ultimoIntervento":"string","pacchetti":[{"nome":"string","da":"string","a":"string"}]}]}}
    apiFetch<{ success: boolean; data: StoricoSistema }>('/api/sistema/storico?giorni=90')
      .then(r => setStorico(r.data))
      .catch(() => setStoricoErrore('Non è stato possibile caricare lo storico di sistema.'))
      .finally(() => setStoricoCaricamento(false));
  };

  useEffect(() => {
    // @api GET /api/sistema/stato -> {"success":true,"data":{"versione":{"build":"string","rilasciatoIl":"string","commit":"string"},"backup":{"database":{...},"documenti":{...}},"restore":{...},"host":{"host":"string","salute":"string","rilevatoIl":"string","distro":"string","kernelInEsecuzione":"string","kernelPiuRecente":"string","kernelDisallineato":"boolean","pacchettiPendenti":"number","riavvioRichiesto":"boolean","aggiornamentiAutomatici":"boolean","riavvioAutomatico":"boolean","orarioRiavvio":"string","prossimaCorsa":"string"}}}
    apiFetch<{ success: boolean; data: StatoSistema }>('/api/sistema/stato')
      .then(r => setSistema(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // L'operativo lavora sempre in una società: senza società selezionata non
    // c'è niente di suo da mostrare.
    if (!canIntegrazioni || (!societaId && !isSuperadmin)) {
      setIntegrazioni(null);
      return;
    }
    const qs = societaId ? `?societaId=${societaId}` : '';
    // @api GET /api/integrazioni/stato?societaId=N -> {"success":true,"data":{"sogliaOre":"number","integrazioni":[{"tipo":"string","nome":"string","modulo":"string","percorso":"string","salute":"string","ultimoAccessoAt":"string","voci":[{"societaId":"number","societaNome":"string","riferimento":"string","salute":"string","ultimoAccessoAt":"string","esitoOk":"boolean","messaggio":"string","origine":"string"}]}]}}
    apiFetch<{ success: boolean; data: StatoIntegrazioni }>(`/api/integrazioni/stato${qs}`)
      .then(r => setIntegrazioni(r.data))
      .catch(() => setIntegrazioni(null));
  }, [canIntegrazioni, societaId, isSuperadmin]);

  useEffect(() => {
    if (!canMessaggi) return;
    // @api GET /api/messaggi/ricevuti -> [{ id, oggetto, priorita, createdAt, mittente_nome, stato }]
    apiFetch<MsgRicevuto[]>('/api/messaggi/ricevuti')
      .then(data => {
        const list = data ?? [];
        setMessaggi(list);
        setNonLetti(list.filter(m => m.stato === 'DA_LEGGERE').length);
      })
      .catch(() => {});
  }, [canMessaggi]);

  useEffect(() => {
    if (!canAssistenza) return;
    // @api GET /api/assistenza/ticket -> { success: true, data: [{ id, tipo, priorita, stato, titolo, modulo, urlPagina, createdByName, createdAt, societa: { id, nome }, _count: { allegati, risposte } }] }
    apiFetch<{ success: boolean; data: TicketSummary[] }>('/api/assistenza/ticket')
      .then(res => setTicket(res.data ?? []))
      .catch(() => {});
  }, [canAssistenza]);

  const statTicket = useMemo(() => {
    const aperte = ticket.filter(t => t.stato === 'APERTO').length;
    const inLavorazione = ticket.filter(t => t.stato === 'IN_LAVORAZIONE').length;
    // Risolti in attesa di conferma: per chi ha aperto il ticket è una cosa da fare
    const daConfermare = ticket.filter(t => t.stato === 'RISOLTO').length;
    const urgenti = ticket.filter(t =>
      (t.priorita === 'BLOCCANTE' || t.priorita === 'URGENTE') &&
      t.stato !== 'CHIUSO' && t.stato !== 'RISOLTO'
    ).length;
    return { aperte, inLavorazione, daConfermare, urgenti };
  }, [ticket]);

  // Ticket attivi: tutto quello che non è chiuso. Un risolto aspetta la conferma
  // di chi l'ha aperto, quindi va in cima, prima ancora della priorità.
  const ticketAttivi = useMemo(() => {
    return [...ticket]
      .filter(t => t.stato !== 'CHIUSO')
      .sort((a, b) => {
        const attesa = Number(b.stato === 'RISOLTO') - Number(a.stato === 'RISOLTO');
        if (attesa !== 0) return attesa;
        const p = PESO_PRIORITA[a.priorita] - PESO_PRIORITA[b.priorita];
        if (p !== 0) return p;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })
      .slice(0, 8);
  }, [ticket]);

  const messaggiTop = useMemo(() => messaggi.slice(0, 8), [messaggi]);

  const oggi = new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Bentornato{utente?.nome ? `, ${utente.nome.split(' ')[0]}` : ''}!</h2>
          <p className="dash-date">{oggi}</p>
        </div>

        <div className="dash-riquadri">
        {integrazioni && integrazioni.integrazioni.length > 0 && (
          <div className="dash-sistema dash-sistema--integrazioni">
            <div className="dash-sistema__intestazione">
              <div className="dash-sistema__titolo">Integrazioni</div>
            </div>

            {integrazioni.integrazioni.map(i => {
              const allarme = descrizioneIntegrazione(i, integrazioni.sogliaOre);
              const ok = i.voci.filter(v => v.salute === 'OK').length;
              return (
                <React.Fragment key={i.tipo}>
                  <div className="dash-sistema__riga">
                    <span className="dash-sistema__etichetta">
                      <span className={`dash-sistema__dot dash-sistema__dot--${CLASSE_INTEGRAZIONE[i.salute]}`} />
                      {can(i.modulo, 'view') ? (
                        <button className="dash-integrazioni__nome" onClick={() => navigate(i.percorso)}>{i.nome}</button>
                      ) : i.nome}
                    </span>
                    <span className="dash-sistema__valore">
                      {valoreIntegrazione(i)}
                      {i.voci.length > 1 && (
                        <span className="dash-sistema__dettaglio"> · {ok}/{i.voci.length} ok</span>
                      )}
                    </span>
                  </div>
                  {allarme && (
                    <div className={`dash-sistema__allarme dash-sistema__allarme--${CLASSE_INTEGRAZIONE[i.salute]}`}>
                      {allarme}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {sistema && (
          <div className="dash-sistema">
            <div className="dash-sistema__intestazione">
              <div className="dash-sistema__titolo">Sistema</div>
              <button className="dash-sistema__storico" onClick={apriStorico}>
                Storico
              </button>
            </div>

            <div className="dash-sistema__riga">
              <span className="dash-sistema__etichetta">Versione</span>
              <span className="dash-sistema__valore">
                {sistema.versione.build ? `build ${sistema.versione.build}` : 'non disponibile'}
                {sistema.versione.rilasciatoIl && (
                  <span className="dash-sistema__dettaglio"> · {formatDataOra(sistema.versione.rilasciatoIl)}</span>
                )}
              </span>
            </div>

            <div className="dash-sistema__riga">
              <span className="dash-sistema__etichetta">
                <span className={`dash-sistema__dot dash-sistema__dot--${CLASSE_SALUTE[sistema.backup.database.salute]}`} />
                Backup DB
              </span>
              <span className="dash-sistema__valore">
                {sistema.backup.database.eseguitoIl ? (
                  <>
                    {formatDataOra(sistema.backup.database.eseguitoIl)}
                    {formatMB(sistema.backup.database.dimensione) && (
                      <span className="dash-sistema__dettaglio"> · {formatMB(sistema.backup.database.dimensione)}</span>
                    )}
                  </>
                ) : 'mai eseguito'}
              </span>
            </div>
            {descrizioneSalute(sistema.backup.database, sistema.backup.oreSoglia) && (
              <div className={`dash-sistema__allarme dash-sistema__allarme--${CLASSE_SALUTE[sistema.backup.database.salute]}`}>
                {descrizioneSalute(sistema.backup.database, sistema.backup.oreSoglia)}
              </div>
            )}

            <div className="dash-sistema__riga">
              <span className="dash-sistema__etichetta">
                <span className={`dash-sistema__dot dash-sistema__dot--${CLASSE_SALUTE[sistema.backup.documenti.salute]}`} />
                Backup documenti
              </span>
              <span className="dash-sistema__valore">
                {sistema.backup.documenti.eseguitoIl ? (
                  <>
                    {formatDataOra(sistema.backup.documenti.eseguitoIl)}
                    {sistema.backup.documenti.numeroFile !== null && (
                      <span className="dash-sistema__dettaglio"> · {sistema.backup.documenti.numeroFile} file</span>
                    )}
                  </>
                ) : 'mai eseguito'}
              </span>
            </div>
            {descrizioneSalute(sistema.backup.documenti, sistema.backup.oreSoglia) && (
              <div className={`dash-sistema__allarme dash-sistema__allarme--${CLASSE_SALUTE[sistema.backup.documenti.salute]}`}>
                {descrizioneSalute(sistema.backup.documenti, sistema.backup.oreSoglia)}
              </div>
            )}

            <div className="dash-sistema__riga">
              <span className="dash-sistema__etichetta">
                <span className={`dash-sistema__dot dash-sistema__dot--${CLASSE_SALUTE[sistema.restore.salute]}`} />
                Prova ripristino
              </span>
              <span className="dash-sistema__valore">
                {sistema.restore.eseguitoIl ? (
                  <>
                    {formatDataOra(sistema.restore.eseguitoIl)}
                    {sistema.restore.tabelleRipristinate !== null && (
                      <span className="dash-sistema__dettaglio">
                        {' · '}{sistema.restore.tabelleRipristinate} tabelle
                        {sistema.restore.tabelleAllineate !== null && `, ${sistema.restore.tabelleAllineate} allineate`}
                      </span>
                    )}
                  </>
                ) : 'mai eseguita'}
              </span>
            </div>
            {descrizioneRestore(sistema.restore) && (
              <div className={`dash-sistema__allarme dash-sistema__allarme--${
                sistema.restore.salute === 'OK' ? 'info' : CLASSE_SALUTE[sistema.restore.salute]
              }`}>
                {descrizioneRestore(sistema.restore)}
              </div>
            )}

            <div className="dash-sistema__riga">
              <span className="dash-sistema__etichetta">
                <span className={`dash-sistema__dot dash-sistema__dot--${CLASSE_SALUTE[sistema.host.salute]}`} />
                Macchina di backup
              </span>
              <span className="dash-sistema__valore">
                {sistema.host.rilevatoIl ? (
                  <>
                    {sistema.host.distro ?? 'sistema non rilevato'}
                    {formatKernel(sistema.host.kernelInEsecuzione) && (
                      <span className="dash-sistema__dettaglio">
                        {' · '}{formatKernel(sistema.host.kernelInEsecuzione)}
                      </span>
                    )}
                    {!!sistema.host.pacchettiPendenti && (
                      <span className="dash-sistema__dettaglio">
                        {' · '}{formatIntero(sistema.host.pacchettiPendenti)} in attesa
                      </span>
                    )}
                  </>
                ) : 'mai riportato'}
              </span>
            </div>
            {descrizioneHost(sistema.host) && (
              <div className={`dash-sistema__allarme dash-sistema__allarme--${descrizioneHost(sistema.host)!.tono}`}>
                {descrizioneHost(sistema.host)!.testo}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* ── Statistiche rapide ─────────────────────────────────────── */}
      <div className="dash-stats">
        {canMessaggi && (
          <button className="dash-stat dash-stat--blue" onClick={() => navigate('/aziendale/messaggi')}>
            <span className="dash-stat__icon"><IconBell /></span>
            <span className="dash-stat__body">
              <span className="dash-stat__num">{nonLetti}</span>
              <span className="dash-stat__label">Messaggi non letti</span>
            </span>
          </button>
        )}
        {canAssistenza && (
          <>
            <button className="dash-stat dash-stat--green" onClick={() => navigate('/assistenza/richieste')}>
              <span className="dash-stat__icon"><IconLifeBuoy /></span>
              <span className="dash-stat__body">
                <span className="dash-stat__num">{statTicket.aperte}</span>
                <span className="dash-stat__label">Richieste aperte</span>
              </span>
            </button>
            <button className="dash-stat dash-stat--amber" onClick={() => navigate('/assistenza/richieste')}>
              <span className="dash-stat__icon"><IconLoader /></span>
              <span className="dash-stat__body">
                <span className="dash-stat__num">{statTicket.inLavorazione}</span>
                <span className="dash-stat__label">In lavorazione</span>
              </span>
            </button>
            {statTicket.daConfermare > 0 && (
              <button className="dash-stat dash-stat--amber" onClick={() => navigate('/assistenza/richieste')}>
                <span className="dash-stat__icon"><IconLifeBuoy /></span>
                <span className="dash-stat__body">
                  <span className="dash-stat__num">{statTicket.daConfermare}</span>
                  <span className="dash-stat__label">{gestisceAssistenza ? 'In attesa di conferma' : 'Da confermare'}</span>
                </span>
              </button>
            )}
            <button className="dash-stat dash-stat--red" onClick={() => navigate('/assistenza/richieste')}>
              <span className="dash-stat__icon"><IconAlert /></span>
              <span className="dash-stat__body">
                <span className="dash-stat__num">{statTicket.urgenti}</span>
                <span className="dash-stat__label">Urgenti / bloccanti da gestire</span>
              </span>
            </button>
          </>
        )}
      </div>

      {/* ── Pannelli ───────────────────────────────────────────────── */}
      <div className="dash-panels">

        {/* Messaggi */}
        {canMessaggi && (
          <div className="dash-panel">
            <div className="dash-panel__header">
              <div className="dash-panel__title">
                <IconBell />
                <span>Messaggi</span>
                {nonLetti > 0 && <span className="dash-panel__count">{nonLetti}</span>}
              </div>
              <button className="dash-panel__link" onClick={() => navigate('/aziendale/messaggi')}>Vai alla casella →</button>
            </div>
            <div className="dash-panel__body">
              {messaggiTop.length === 0 ? (
                <div className="dash-panel__empty">Nessun messaggio ricevuto</div>
              ) : messaggiTop.map(m => (
                <div
                  key={m.id}
                  className={`dash-row ${m.stato === 'DA_LEGGERE' ? 'dash-row--unread' : ''}`}
                  onClick={() => navigate('/aziendale/messaggi')}
                >
                  <span className="dash-row__lead">
                    {m.stato === 'DA_LEGGERE' ? <span className="dash-unread-dot" /> : null}
                  </span>
                  <div className="dash-row__main">
                    <div className={`dash-row__title ${m.stato === 'DA_LEGGERE' ? 'dash-row__title--unread' : ''}`}>
                      {m.oggetto}
                    </div>
                    <div className="dash-row__meta">
                      <span>Da: {m.mittente_nome}</span>
                      <span>{formatDate(m.createdAt)}</span>
                    </div>
                  </div>
                  <div className="dash-row__side">
                    {/* Colore dinamico per priorità — deve restare inline */}
                    <span className="dash-msg-prio" style={{ color: PRIORITA_COLOR[m.priorita], background: (PRIORITA_COLOR[m.priorita] || '#6b7280') + '22' }}>
                      {PRIORITA_LABEL[m.priorita] || m.priorita}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Richieste assistenza */}
        {canAssistenza && (
          <div className="dash-panel">
            <div className="dash-panel__header">
              <div className="dash-panel__title">
                <IconLifeBuoy />
                <span>Richieste assistenza</span>
                {ticketAttivi.length > 0 && <span className="dash-panel__count">{ticketAttivi.length}</span>}
              </div>
              <button className="dash-panel__link" onClick={() => navigate('/assistenza/richieste')}>Vedi tutte →</button>
            </div>
            <div className="dash-panel__body">
              {ticketAttivi.length === 0 ? (
                <div className="dash-panel__empty">Nessuna richiesta attiva</div>
              ) : ticketAttivi.map(t => (
                <div
                  key={t.id}
                  className="dash-row"
                  onClick={() => navigate(`/assistenza/richieste/${t.id}`)}
                >
                  <span className="dash-row__lead"><span className="dash-row__id">#{t.id}</span></span>
                  <div className="dash-row__main">
                    <div className="dash-row__title">{t.titolo}</div>
                    <div className="dash-row__meta">
                      <span>{TICKET_TIPO_LABEL[t.tipo]}</span>
                      {gestisceAssistenza && <span>{t.createdByName}</span>}
                      {t.modulo && <span>{t.modulo}</span>}
                      <span>{new Date(t.createdAt).toLocaleDateString('it-IT')}</span>
                    </div>
                  </div>
                  <div className="dash-row__side">
                    <div className="dash-row__badges">
                      <span className={`bop-badge ${ticketPrioritaBadge(t.priorita)}`}>{TICKET_PRIORITA_LABEL[t.priorita]}</span>
                      <span className={`bop-badge ${ticketStatoBadge(t.stato)}`}>{ticketStatoTesto(t.stato, gestisceAssistenza)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {storicoAperto && (
        <StoricoSistemaModal
          storico={storico}
          host={sistema?.host ?? null}
          caricamento={storicoCaricamento}
          errore={storicoErrore}
          onClose={() => setStoricoAperto(false)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
